export const UNMATCHED_FILES_COLUMNS = [
  {
    key: 'status',
    label: 'Trust Status',
    required: true,
    sortable: true,
    sortKey: 'trustStatus',
    tooltip: 'Trust status and review note for this unmatched file.',
  },
  {
    key: 'fileName',
    label: 'File Name',
    required: true,
    sortable: true,
    sortKey: 'fileName',
    tooltip: 'Best-effort file name and SHA-256 hash for this unmatched file.',
  },
  {
    key: 'authenticodeHash',
    label: 'Authenticode Hash',
    tooltip: 'Authenticode SHA-256 hash, the hash WDAC hash rules match on.',
  },
  {
    key: 'matchedApps',
    label: 'Matched Apps',
    sortable: true,
    sortKey: 'matchedApps',
    tooltip:
      'Number of trusted global apps in this environment whose exact file hash matches this file.',
  },
  {
    key: 'signers',
    label: 'Total Signers',
    sortable: true,
    sortKey: 'signers',
    tooltip: 'Number of distinct signers seen for this file.',
  },
  {
    key: 'trustedSigners',
    label: 'Trusted Signers',
    sortable: true,
    sortKey: 'trustedSigners',
    tooltip: 'Signers whose issuer and publisher hashes match signers used in trusted apps.',
  },
  {
    key: 'trustedPublisherNames',
    label: 'Trusted Publisher Names',
    sortable: true,
    sortKey: 'trustedPublisherNames',
    tooltip:
      'Signers with issuer hash matches where publisher hash does not, but publisher name matches.',
  },
  {
    key: 'trustedIssuers',
    label: 'Trusted Issuers',
    sortable: true,
    sortKey: 'trustedIssuers',
    tooltip: 'Signers with issuer hash matches from trusted apps.',
  },
  {
    key: 'launchingProcess',
    label: 'Launching Process',
    sortable: true,
    sortKey: 'launchingProcess',
    tooltip: 'Most common process that launched this file based on collected events.',
  },
  {
    key: 'directory',
    label: 'Directory',
    sortable: true,
    sortKey: 'directory',
    tooltip: 'Most common directory seen for this file.',
  },
  {
    key: 'lastSeen',
    label: 'Last Seen',
    sortable: true,
    sortKey: 'lastSeen',
  },
  {
    key: 'devices',
    label: 'Devices',
    sortable: true,
    sortKey: 'devices',
    tooltip: 'Devices with events within 30 and 90 days of last seen. Sorts by the last 30 count.',
  },
  {
    key: 'noteColumn',
    label: 'Note',
    tooltip: 'Note stored for this unmatched file.',
  },
];

export const DEFAULT_UNMATCHED_FILES_VISIBLE_COLUMNS = [
  'status',
  'fileName',
  'launchingProcess',
  'directory',
  'lastSeen',
  'devices',
];

export const UNMATCHED_FILE_LAST_SEEN_RANGE = {
  All: '',
  Last30Days: '0-30',
  Days30To60: '30-60',
  Days60Plus: '60+',
};

export const UNMATCHED_FILE_LAST_SEEN_RANGE_OPTIONS = [
  { value: UNMATCHED_FILE_LAST_SEEN_RANGE.All, label: 'All last seen dates' },
  { value: UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days, label: '0-30 days ago' },
  { value: UNMATCHED_FILE_LAST_SEEN_RANGE.Days30To60, label: '30-60 days ago' },
  { value: UNMATCHED_FILE_LAST_SEEN_RANGE.Days60Plus, label: '60+ days ago' },
];

export const UNMATCHED_FILE_TABLE_LAST_SEEN_RANGE_OPTIONS = [
  { value: UNMATCHED_FILE_LAST_SEEN_RANGE.All, label: 'All last seen dates' },
  { value: UNMATCHED_FILE_LAST_SEEN_RANGE.Last30Days, label: '0-30 days' },
  { value: UNMATCHED_FILE_LAST_SEEN_RANGE.Days30To60, label: '30-60 days' },
  { value: UNMATCHED_FILE_LAST_SEEN_RANGE.Days60Plus, label: '60+ days' },
];

export const getUnmatchedFileLastSeenRangeLabel = value =>
  UNMATCHED_FILE_LAST_SEEN_RANGE_OPTIONS.find(option => option.value === value)?.label ||
  'All last seen dates';

export const UNMATCHED_FILE_TRUST_SIGNAL = {
  All: '',
  MatchedApps: 'matchedApps',
  TrustedSigners: 'trustedSigners',
  TrustedPublisherNames: 'trustedPublisherNames',
  TrustedIssuers: 'trustedIssuers',
  SignedByUnknownCert: 'signedByUnknownCert',
  Unsigned: 'unsigned',
};

export const UNMATCHED_FILE_TRUST_SIGNAL_OPTIONS = [
  { value: UNMATCHED_FILE_TRUST_SIGNAL.All, label: 'All trust signals' },
  { value: UNMATCHED_FILE_TRUST_SIGNAL.MatchedApps, label: 'Matched Apps' },
  { value: UNMATCHED_FILE_TRUST_SIGNAL.TrustedSigners, label: 'Trusted Signers' },
  {
    value: UNMATCHED_FILE_TRUST_SIGNAL.TrustedPublisherNames,
    label: 'Trusted Publisher Names',
  },
  {
    value: UNMATCHED_FILE_TRUST_SIGNAL.TrustedIssuers,
    label: 'Trusted Issuers',
  },
  { value: UNMATCHED_FILE_TRUST_SIGNAL.SignedByUnknownCert, label: 'Signed by Unknown Cert' },
  { value: UNMATCHED_FILE_TRUST_SIGNAL.Unsigned, label: 'Unsigned' },
];

export const UNMATCHED_FILE_TRUST_SIGNAL_COUNT_LABEL = {
  [UNMATCHED_FILE_TRUST_SIGNAL.MatchedApps]: 'Files in Matched Apps',
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedSigners]: 'Files with Trusted Signers',
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedPublisherNames]: 'Files with Trusted Publisher Names',
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedIssuers]: 'Files with Trusted Issuers',
  [UNMATCHED_FILE_TRUST_SIGNAL.SignedByUnknownCert]: 'Signed by Unknown Cert',
  [UNMATCHED_FILE_TRUST_SIGNAL.Unsigned]: 'Unsigned Files',
};

const SIGNER_GROUPING_NOTE =
  'A file can be signed by more than one certificate, so the same file may appear under multiple signers.';
const APP_GROUPING_NOTE =
  'A file can belong to more than one application, so the same file may appear under multiple apps.';

export const TRUST_SIGNAL_INFO = {
  [UNMATCHED_FILE_TRUST_SIGNAL.MatchedApps]: {
    title: 'Matched Apps (Highly Trustable)',
    description:
      "These files were seen on your devices but each file's exact hash matches a file found in one of your approved applications. These files are safe to trust with no additional risk.",
    groupingNote: APP_GROUPING_NOTE,
  },
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedSigners]: {
    title: 'Trusted Signers (Highly Trustable)',
    description:
      'These files are signed with a certificate that exactly matches the issuer & publisher certificate combination of one of your approved applications. Trusting these files is low risk.',
    groupingNote: SIGNER_GROUPING_NOTE,
  },
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedPublisherNames]: {
    title: 'Trusted Publisher Names (To be reviewed)',
    description:
      'These files share a publisher name and certificate issuer combination with a file in one of your approved applications, but the exact certificate is different. Review the Issuer Name & Publisher Name combination.',
    groupingNote: SIGNER_GROUPING_NOTE,
  },
  [UNMATCHED_FILE_TRUST_SIGNAL.TrustedIssuers]: {
    title: 'Trusted Issuers (Careful Review Required)',
    description:
      'These files are signed by a certificate issued by an issuer that is present in one of your approved applications. There is no exact publisher certificate or name match; only the issuer is shared, typically demonstrating this is a genuine issuer.',
    groupingNote: SIGNER_GROUPING_NOTE,
  },
  [UNMATCHED_FILE_TRUST_SIGNAL.SignedByUnknownCert]: {
    title: 'Signed by Unknown Cert (Careful Review Required)',
    description:
      'These files are signed, but their certificate does not match a file in any approved application through issuer, publisher or a combination thereof.',
    groupingNote: SIGNER_GROUPING_NOTE,
  },
  [UNMATCHED_FILE_TRUST_SIGNAL.Unsigned]: {
    title: 'Unsigned (Strictest Review Required)',
    description:
      'These files carry no digital signature at all. Without a certificate, there is no way to automatically verify the origin or integrity of these files. Only trust these files after careful vetting.',
  },
};

// IssuerName is supported by the API but intentionally not surfaced as a frontend group-by option.
export const UNMATCHED_FILE_GROUP_BY = {
  PublisherName: 'PublisherName',
  IssuerName: 'IssuerName',
  TbsHash: 'TbsHash',
  IssuerTbsHash: 'IssuerTbsHash',
};

export const UNMATCHED_FILE_GROUP_BY_OPTIONS = [
  { value: UNMATCHED_FILE_GROUP_BY.TbsHash, label: 'Publisher TBS Hash' },
  { value: UNMATCHED_FILE_GROUP_BY.PublisherName, label: 'Publisher Name' },
  { value: UNMATCHED_FILE_GROUP_BY.IssuerTbsHash, label: 'Issuer TBS Hash' },
];

export const DEFAULT_UNMATCHED_FILE_GROUP_BY = UNMATCHED_FILE_GROUP_BY.TbsHash;
